import nebulaSvg from '@discoveryedu/nebula-icons/nebula.svg';

const ESCAPE_MAP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function escapeHtml(text) {
  return text.replace(/[&<>"']/g, (char) => ESCAPE_MAP[char]);
}

// Coerced through Number() so only a real integer, never a quote-breaking string, is ever emitted.
function safeSpanAttr(name, value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 1 ? ` ${name}="${n}"` : '';
}

// Shared by tableCell/tableHeader -- both carry the same colspan/rowspan attrs.
function tableCellAttrs(node) {
  const { colspan, rowspan } = node.attrs ?? {};
  return `${safeSpanAttr('colspan', colspan)}${safeSpanAttr('rowspan', rowspan)}`;
}

// Studio's Align toolbar stores it as an `align-*` token inside paragraph.attrs.class
// (via toggleClassParagraph); manuscript-imported paragraphs can instead carry a plain
// paragraph.attrs.textAlign (ScriptLab's own @tiptap/extension-text-align). Check both.
const ALIGN_CLASS_RE = /\balign-(left|center|right)\b/;

function paragraphTextAlign(node) {
  const attrs = node.attrs ?? {};
  if (attrs.textAlign === 'left' || attrs.textAlign === 'center' || attrs.textAlign === 'right') {
    return attrs.textAlign;
  }
  return attrs.class?.match(ALIGN_CLASS_RE)?.[1] ?? null;
}

// Fixed order (not JSON order) so nested tags stay consistent, e.g. always <strong><em>.
const MARK_TAGS = [
  ['bold', 'strong'],
  ['italic', 'em'],
  ['underline', 'u'],
  ['strike', 's'],
  ['subscript', 'sub'],
  ['superscript', 'sup'],
  ['highlight', 'mark'],
];

// link/button are navigation marks in the real editor; flagged visually here, no real anchor/navigation.
const LINK_LIKE_MARK_TYPES = new Set(['link', 'button']);

// textColor is intentionally unhandled -- it's a small closed set today, but that's an
// artifact of the current theme roster, not a stable contract; skip it entirely rather
// than chase every theme's palette.
function wrapMarks(html, marks) {
  if (!Array.isArray(marks) || !marks.length) return html;
  const types = new Set(marks.map((mark) => mark.type));
  let wrapped = html;
  MARK_TAGS.forEach(([type, tag]) => {
    if (types.has(type)) wrapped = `<${tag}>${wrapped}</${tag}>`;
  });
  if ([...types].some((type) => LINK_LIKE_MARK_TYPES.has(type))) {
    wrapped = `<span class="review-change-rich-text__link-mark">${wrapped}</span>`;
  }
  return wrapped;
}

// Exposed for reviewChangesDiff.js's text_diff merge to render a run outside a full doc walk.
export function renderMarkedText(text, marks) {
  return text ? wrapMarks(escapeHtml(text), marks) : '';
}

// Same <svg><use> markup NebulaIcon.vue renders, against the same bundled sprite --
// works as static HTML with no Vue component. symbolId is escaped (string-concatenated into href).
export function renderNebulaIcon(symbolId) {
  if (!symbolId) return '';
  return '<svg class="nebula-icon nebula-icon--s review-change-rich-text__icon" aria-hidden="true" focusable="false">'
    + `<use xlink:href="${escapeHtml(nebulaSvg)}#${escapeHtml(symbolId)}"></use></svg>`;
}

// StudioTextEditor.vue registers both -- 'hardBreak' is the live default, 'hard_break' a
// legacy alias kept for old content.
const HARD_BREAK_TYPES = new Set(['hardBreak', 'hard_break']);

// Leaf runs for reviewChangesDiff.js's alignRunsWithSegments; icon and hard-break runs
// are zero-width since backend's text_diff counts both as 0 characters.
export function flattenParagraphRuns(node) {
  if (!Array.isArray(node?.content)) return [];
  return node.content.flatMap((child) => {
    if (child.type === 'text') return child.text ? [{ text: child.text, marks: child.marks }] : [];
    if (HARD_BREAK_TYPES.has(child.type)) return [{ break: true }];
    if (child.type === 'nebulaIconNode') return [{ text: '', marks: [], icon: child.attrs?.symbolId }];
    return [];
  });
}

// Renders a ProseMirror doc as escaped HTML, safe for v-html; an unrecognized block
// type renders just its children.
// options.renderParagraph(node, index) overrides one paragraph's inner HTML (index =
// doc order); return null to keep default rendering. Lets reviewChangesDiff.js splice
// text_diff highlighting onto the real doc.
export function proseMirrorToHtml(node, options = {}) {
  let nextParagraphIndex = 0;
  function render(current) {
    if (!current) return '';
    if (current.type === 'text') return renderMarkedText(current.text, current.marks);
    if (HARD_BREAK_TYPES.has(current.type)) return '<br>';
    if (current.type === 'nebulaIconNode') return renderNebulaIcon(current.attrs?.symbolId);
    const children = Array.isArray(current.content) ? current.content.map(render).join('') : '';
    switch (current.type) {
      case 'paragraph': {
        let inner = children;
        if (options.renderParagraph) {
          const index = nextParagraphIndex;
          nextParagraphIndex += 1;
          const override = options.renderParagraph(current, index);
          if (override != null) inner = override;
        }
        const align = paragraphTextAlign(current);
        return align ? `<p style="text-align: ${align}">${inner}</p>` : `<p>${inner}</p>`;
      }
      case 'heading': {
        const level = Math.min(Math.max(Number(current.attrs?.level) || 1, 1), 6);
        return `<h${level}>${children}</h${level}>`;
      }
      case 'blockquote': return `<blockquote>${children}</blockquote>`;
      case 'bulletList': return `<ul>${children}</ul>`;
      case 'orderedList': return `<ol>${children}</ol>`;
      case 'listItem': return `<li>${children}</li>`;
      case 'table': return `<table>${children}</table>`;
      case 'tableRow': return `<tr>${children}</tr>`;
      case 'tableCell': return `<td${tableCellAttrs(current)}>${children}</td>`;
      case 'tableHeader': return `<th${tableCellAttrs(current)}>${children}</th>`;
      default: return children;
    }
  }
  return render(node);
}

export function hasProseMirrorText(node) {
  if (!node) return false;
  if (node.type === 'text') return !!node.text;
  if (node.type === 'nebulaIconNode') return !!node.attrs?.symbolId;
  return Array.isArray(node.content) && node.content.some(hasProseMirrorText);
}

export default proseMirrorToHtml;
